import React, { useEffect, useRef, useState } from 'react'
import { When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { CaptionSM } from '@/atoms/Text'
import { useAngelPlayerContext } from '@/organisms/AngelPlayer'
import { CollectibleSceneOwnership } from '@/organisms/CollectibleSceneInfo/CollectibleSceneDetails/CollectibleSceneOwnership'
import {
  getDedupedOwners,
  sortOwners,
} from '@/organisms/CollectibleSceneInfo/CollectibleSceneDetails/CollectibleSceneOwnership/util'
import { CollectibleSceneStories } from '@/organisms/CollectibleSceneInfo/CollectibleSceneDetails/CollectibleSceneStories'
import { useCollectiblesPlayerEngagement } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext'
import { MomentUserStory } from '@/services/MomentsService/types'
import { useUser } from '@/services/UserService'
import { useLoginUrl } from '@/utils/auth-utils'
import { useTranslate } from '@/utils/translate/translate-client'
import { AddANewStory } from './AddANewStory'
import FrameOwnerSection from './CollectibleSceneOwnership/FrameOwnerSection'
import { TextStory } from './TextStory'

export const CollectibleSceneDetails: React.FC = () => {
  const { isLoggedIn, loading: isLoadingUser, userId } = useUser()
  const { loginUrl, trackLoginStarted } = useLoginUrl()
  const { t } = useTranslate('watch')
  const { engagementMomentDetails, isLoadingEngagementMomentDetails, userStoryId } = useCollectiblesPlayerEngagement()
  const { asset } = useAngelPlayerContext()

  const [sortedTextStories, setSortedTextStories] = useState<MomentUserStory[]>([])
  const isSortedRef = useRef(false)

  const actors = engagementMomentDetails?.moment?.actors ?? []
  const owners = getDedupedOwners(engagementMomentDetails?.moment?.owners, actors, userId)
  const sortedOwners = sortOwners(owners)
  const isOwner = Boolean(engagementMomentDetails?.moment?.isOwner)
  const textStories = engagementMomentDetails?.userStories?.stories?.filter((story) => story?.text !== null)
  const videoStories = engagementMomentDetails?.userStories?.stories?.filter((story) => story?.videoUri !== null)
  const moment = engagementMomentDetails?.moment

  const [showAllTextStories, setShowAllTextStories] = useState(true)

  useEffect(() => {
    isSortedRef.current = false
  }, [moment])

  useEffect(() => {
    if (textStories && !isSortedRef.current) {
      const sorted = [...textStories].sort((a, b) => (b?.totalLikes ?? 0) - (a?.totalLikes ?? 0))
      setSortedTextStories(sorted.filter((story) => story !== null) as MomentUserStory[])
      isSortedRef.current = true
    }
  }, [textStories])

  const userStory = userStoryId ? textStories?.find((story) => story?.id === userStoryId) : undefined

  const mostPopularTextStory = sortedTextStories[0]
  const featuredStory = userStory ?? mostPopularTextStory

  return (
    <>
      <FrameOwnerSection />
      <CollectibleSceneOwnership
        projectName={asset.projectName}
        totalNumberOfOwners={engagementMomentDetails?.moment?.totalNumberOfOwners ?? 0}
        owners={sortedOwners}
        isOwner={isOwner}
        loading={isLoadingEngagementMomentDetails}
        actors={actors}
        myStory={engagementMomentDetails?.moment?.userStory ?? engagementMomentDetails?.myStory?.stories?.[0]}
        momentId={moment?.id}
        availableFrames={moment?.availableFrames}
      />
      <When condition={!isLoadingUser && !isLoggedIn}>
        <LinkButton
          href={loginUrl}
          variant="white"
          className="mb-6 justify-center lg:w-fit"
          outline
          onClick={() => trackLoginStarted('collectible-scene-panel')}
        >
          {t('logInToViewFullOwnership', 'Log In To View Full Ownership')}
        </LinkButton>
      </When>
      <div className="flex flex-col items-start">
        <CollectibleSceneStories totalStories={videoStories?.length ?? 0} stories={videoStories} />
        <div className="relative mb-6 w-full rounded-xl bg-core-gray-800 p-4">
          <AddANewStory />
          {showAllTextStories && sortedTextStories?.length ? (
            <div className="h-[400px] overflow-y-scroll scrollbar-thin scrollbar-track-core-gray-900 scrollbar-thumb-core-gray-800">
              {sortedTextStories?.map((story) => (
                <TextStory key={story?.id} story={story} projectName={asset.projectName} />
              ))}
            </div>
          ) : (
            <TextStory story={featuredStory} projectName={asset.projectName} />
          )}
          {(textStories?.length ?? 0) > 1 && (
            <CaptionSM onClick={() => setShowAllTextStories(!showAllTextStories)} className="cursor-pointer pt-3">
              {!showAllTextStories ? <>{t('showMore', 'Show more')}</> : <>{t('showLess', 'Show less')}</>}
            </CaptionSM>
          )}
        </div>
      </div>
    </>
  )
}
